/**
 * Activity Tab Component
 *
 * PE-769: Displays chronological activity timeline for any record.
 * Shows creation, updates, status changes, relationships, and subtask activity.
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getActivityForRecord,
  type ActivityEntry,
} from '../../services/activityLogService';
import {
  Plus,
  Edit3,
  Trash2,
  GitBranch,
  CheckCircle2,
  UserPlus,
  Link as LinkIcon,
  Unlink,
  Clock,
  ChevronDown,
  ChevronRight,
  FileText,
  RotateCcw,
  Activity,
  MessageSquare,
} from 'lucide-react';

interface ActivityTabProps {
  /** Record ID to show activity for */
  recordId: string;
  /** Object type (e.g., 'Client__c', 'Task__c') */
  objectType: string;
  /** Optional record name for display */
  recordName?: string;
}

export const ActivityTab: React.FC<ActivityTabProps> = ({
  recordId,
  objectType,
  recordName,
}) => {
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());
  const [filterAction, setFilterAction] = useState<string>('all');

  // Load activity
  const { data: activities = [], isLoading, refetch } = useQuery({
    queryKey: ['activity', recordId, objectType],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 100));
      return getActivityForRecord(recordId, objectType);
    },
  });

  // Filter activities
  const filteredActivities = useMemo(() => {
    if (filterAction === 'all') return activities;
    return activities.filter(entry => entry.action === filterAction);
  }, [activities, filterAction]);

  // Get unique actions for filter
  const availableActions = useMemo(() => {
    const actions = new Set(activities.map(a => a.action));
    return Array.from(actions);
  }, [activities]);

  const toggleExpanded = (entryId: string) => {
    setExpandedEntries(prev => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'created':
        return <Plus className="w-4 h-4" />;
      case 'updated':
        return <Edit3 className="w-4 h-4" />;
      case 'deleted':
        return <Trash2 className="w-4 h-4" />;
      case 'status_changed':
        return <GitBranch className="w-4 h-4" />;
      case 'assigned':
        return <UserPlus className="w-4 h-4" />;
      case 'related_added':
        return <LinkIcon className="w-4 h-4" />;
      case 'related_removed':
        return <Unlink className="w-4 h-4" />;
      case 'subtask_created':
        return <Plus className="w-4 h-4" />;
      case 'subtask_completed':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'subtask_reopened':
        return <RotateCcw className="w-4 h-4" />;
      case 'note_added':
        return <FileText className="w-4 h-4" />;
      case 'comment_added':
        return <MessageSquare className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'created':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'updated':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'deleted':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'status_changed':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      case 'assigned':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'related_added':
        return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
      case 'related_removed':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400';
      case 'subtask_created':
        return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400';
      case 'subtask_completed':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'subtask_reopened':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      case 'note_added':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const getActionLabel = (entry: ActivityEntry) => {
    switch (entry.action) {
      case 'created':
        return 'created this record';
      case 'updated':
        const fieldCount = entry.fieldChanges?.length || 0;
        return `updated ${fieldCount} field${fieldCount !== 1 ? 's' : ''}`;
      case 'deleted':
        return 'deleted this record';
      case 'status_changed':
        return (
          <>
            changed status from{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              "{entry.oldStatus}"
            </span>{' '}
            to{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              "{entry.newStatus}"
            </span>
          </>
        );
      case 'assigned':
        const assignChange = entry.fieldChanges?.[0];
        if (assignChange) {
          return (
            <>
              reassigned from{' '}
              <span className="font-medium">{assignChange.oldValue || 'Unassigned'}</span>{' '}
              to{' '}
              <span className="font-medium">{assignChange.newValue}</span>
            </>
          );
        }
        return 'changed assignment';
      case 'related_added':
        return (
          <>
            linked{' '}
            <span className="font-medium text-indigo-600 dark:text-indigo-400">
              {entry.relatedObjectType?.replace('__c', '')}
            </span>{' '}
            "{entry.relatedRecordName}"
          </>
        );
      case 'related_removed':
        return (
          <>
            unlinked{' '}
            <span className="font-medium">
              {entry.relatedObjectType?.replace('__c', '')}
            </span>{' '}
            "{entry.relatedRecordName}"
          </>
        );
      case 'subtask_created':
        return (
          <>
            created subtask{' '}
            <span className="font-medium text-teal-600 dark:text-teal-400">
              "{entry.metadata?.subtaskTitle}"
            </span>
          </>
        );
      case 'subtask_completed':
        return (
          <>
            completed subtask{' '}
            <span className="font-medium text-green-600 dark:text-green-400">
              "{entry.metadata?.subtaskTitle}"
            </span>
          </>
        );
      case 'subtask_reopened':
        return (
          <>
            reopened subtask{' '}
            <span className="font-medium text-orange-600 dark:text-orange-400">
              "{entry.metadata?.subtaskTitle}"
            </span>
          </>
        );
      case 'note_added':
        return 'added a note';
      case 'comment_added':
        return 'added a comment';
      default:
        return (entry.action as string).replace(/_/g, ' ');
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return '(empty)';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-4">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
              <div className="flex-1 h-16 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
        <div className="text-center">
          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Activity Yet
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Activity will appear here as changes are made to this record.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with filter */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Activity Timeline ({filteredActivities.length})
        </h3>

        {availableActions.length > 1 && (
          <select
            value={filterAction}
            onChange={e => setFilterAction(e.target.value)}
            className="px-3 py-1.5 text-sm bg-white dark:bg-[#0f172a] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Activity</option>
            {availableActions.map(action => (
              <option key={action} value={action}>
                {action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-5 top-3 bottom-3 w-0.5 bg-gray-200 dark:bg-gray-700" />

        {/* Activity entries */}
        <div className="space-y-4">
          {filteredActivities.map((entry) => {
            const isExpanded = expandedEntries.has(entry.id);
            const hasDetails = entry.fieldChanges && entry.fieldChanges.length > 0;

            return (
              <div key={entry.id} className="relative pl-12">
                {/* Icon */}
                <div
                  className={`absolute left-2 top-2 p-1.5 rounded-full ${getActionColor(
                    entry.action
                  )}`}
                >
                  {getActionIcon(entry.action)}
                </div>

                {/* Content */}
                <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 dark:text-white">
                        <span className="font-semibold">{entry.userName}</span>{' '}
                        <span className="text-gray-600 dark:text-gray-400">
                          {getActionLabel(entry)}
                        </span>
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTimestamp(entry.timestamp)}
                      </p>
                    </div>

                    {hasDetails && (
                      <button
                        onClick={() => toggleExpanded(entry.id)}
                        className="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title={isExpanded ? 'Hide details' : 'Show details'}
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>

                  {/* Field changes (expandable) */}
                  {hasDetails && isExpanded && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="space-y-2">
                        {entry.fieldChanges?.map((change, idx) => (
                          <div
                            key={idx}
                            className="flex items-start gap-3 text-xs"
                          >
                            <span className="font-medium text-gray-700 dark:text-gray-300 min-w-[120px] flex-shrink-0">
                              {change.fieldLabel}:
                            </span>
                            <div className="flex-1 flex items-center gap-2 flex-wrap">
                              <span className="text-gray-500 dark:text-gray-500 line-through">
                                {formatValue(change.oldValue)}
                              </span>
                              <span className="text-gray-400">→</span>
                              <span className="text-gray-900 dark:text-white font-medium">
                                {formatValue(change.newValue)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Note preview */}
                  {entry.action === 'note_added' && entry.metadata?.notePreview && (
                    <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs text-gray-600 dark:text-gray-400 italic">
                      "{entry.metadata.notePreview}"
                      {entry.metadata.notePreview.length >= 100 && '...'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Load more indicator */}
      {filteredActivities.length >= 50 && (
        <div className="text-center pt-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing last 50 activities
          </p>
        </div>
      )}
    </div>
  );
};

export default ActivityTab;
